import React, { useEffect, useRef, useState } from 'react';
import InfiniteScrollBox from '../../InfiniteScroll/InfiniteScrollBox';
import {
	GetFeaturedEventSeries,
	EventSeriesWithNextOccurrence,
} from 'Services/Api/_HumanWritten/EventSeriesEntityService';
import { useOrganisationStore } from 'Global/Organisations/OrganisationsStore';
import { EventSeriesCard } from '../EventCards/EventSeriesCard';
import { observer } from 'mobx-react';
import If from '../../../If/If';
import { calculateNumberOfFeaturedEvents } from '../EventTabs/EventTabs';

export interface FeaturedEventsListProps {
	refresh: boolean;
}

const FeaturedEventsList = ({ refresh }: FeaturedEventsListProps) => {
	const orgStore = useOrganisationStore();

	const [featuredEvents, setFeaturedEvents] = useState<EventSeriesWithNextOccurrence[]>([]);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const [empty, setEmpty] = useState(false);
	const [initialized, setInitialized] = useState(false);

	const fetchingRef = useRef(false);

	const fetchMoreData = async (clearList: boolean = false) => {
		if (fetchingRef.current) return; // Prevent duplicate calls

		fetchingRef.current = true;
		try {
			const moreFeaturedEvents = await GetFeaturedEventSeries(
				orgStore.selectedOrganisation?.id,
				clearList ? 0 : featuredEvents.length,
				calculateNumberOfFeaturedEvents(),
			);
			if (moreFeaturedEvents.length < 5) {
				setHasMore(false);
			}

			if (featuredEvents.length === 0 && moreFeaturedEvents.length === 0) {
				setEmpty(true);
			} else {
				setEmpty(false);
			}

			if (clearList) {
				setFeaturedEvents(moreFeaturedEvents);
			} else {
				setFeaturedEvents(prevItems => prevItems.concat(moreFeaturedEvents));
			}
		} catch (error) {
			console.error(error);
		} finally {
			fetchingRef.current = false;
		}
	};

	// Initial fetch on mount only
	useEffect(() => {
		if (!initialized) {
			setLoading(true);
			setInitialized(true);
			fetchMoreData(true).then(() => setLoading(false));
		}
	}, [initialized]);

	// Refetch when refresh changes (but skip initial mount)
	useEffect(() => {
		if (initialized) {
			setLoading(true);
			fetchMoreData(true).then(() => {
				setLoading(false);
			});
		}
	}, [refresh, orgStore.selectedOrganisation?.id]);

	if (empty || loading) {
		return null;
	}

	return (
		<div className="featured-events-list">
			<If condition={featuredEvents.length > 0}>
				<h3>Featured experiences</h3>
				<InfiniteScrollBox
					id="featured-events-scrollable-container"
					items={featuredEvents}
					hasMore={hasMore}
					fetchMoreData={fetchMoreData}
					renderItem={(item, index) => <EventSeriesCard eventSeries={item} index={index} />}
					loader=""
					endMessage=""
					direction="horizontal"
				/>
			</If>
		</div>
	);
};

export default observer(FeaturedEventsList);
